import { useRef, type Ref, type SubmitEvent } from "react";
import { useTranslation } from "react-i18next";

import type { Calendar } from "@/api/bale.zod";

import { useCreateCalendar, useDeleteCalendar, useListCalendars } from "@/api/bale";
import { ActionBox } from "@/components/ActionBox";
import { Button } from "@/components/Button";
import { CardSubtitleHeader } from "@/components/Card";
import { CopyButton } from "@/components/CopyButton";
import { Dialog } from "@/components/Dialog";
import { Form } from "@/components/Form";
import { Icon } from "@/components/Icon";
import { IconButton } from "@/components/IconButton";
import { Input } from "@/components/Input";
import { Missing } from "@/components/Missing";

export function ManageCalendarsModal({ ref, onClose }: { ref: Ref<HTMLDialogElement>; onClose?: () => void }) {
  const { t } = useTranslation();
  const form = useRef<HTMLFormElement>(null);
  const calendars = useListCalendars({ query: { select: (data) => data.data.calendars } });

  const createCalendar = useCreateCalendar({
    mutation: {
      onSuccess: () => form.current?.reset(),
    },
  });
  function handleCreate(event: SubmitEvent<HTMLFormElement>) {
    const form = new FormData(event.currentTarget);
    const name = form.get("name")! as string;
    createCalendar.mutate({ data: { name } });
  }

  return (
    <Dialog ref={ref} onClose={onClose} closeOnOutside className="w-2xl">
      <CardSubtitleHeader>{t("manage-calendars")}</CardSubtitleHeader>
      <div className="flex flex-col">
        <p className="px-4 font-disinterest">{t("manage-calendars-description")}</p>
        <p className="mt-4 mb-2 px-4 font-hint">{t("existing-calendars")}</p>
        <div className="flex flex-col border-t border-divider empty:hidden">
          {calendars.isSuccess &&
            calendars.data.map((calendar) => <CalendarItem key={calendar.id} calendar={calendar} />)}
        </div>
        {calendars.isSuccess && calendars.data.length === 0 && <Missing>{t("no-calendars")}</Missing>}
        <Form
          ref={form}
          error={createCalendar.error}
          className="flex flex-row flex-wrap gap-x-4 gap-y-2 p-4"
          onSubmit={handleCreate}
        >
          <div className="grow-999">
            <Input name="name" type="text" placeholder={t("calendar-name-placeholder")} />
          </div>
          <Button className="min-w-0 grow-1 truncate" type="submit">
            {t("create-calendar")}
          </Button>
        </Form>
      </div>
    </Dialog>
  );
}

function CalendarItem({ calendar }: { calendar: Calendar }) {
  const { t } = useTranslation();
  const deleteCalendar = useDeleteCalendar();

  return (
    <div className="flex flex-row flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-divider px-4 py-2 text-start">
      <p className="font-interest">{calendar.name}</p>
      <ActionBox>
        <CopyButton value={calendar.attending_url}>
          <Icon name="link" />
          {t("copy-link-attending")}
        </CopyButton>
        <CopyButton value={calendar.visible_url}>
          <Icon name="link" />
          {t("copy-link-invited")}
        </CopyButton>
        <IconButton
          type="button"
          className="text-danger"
          name="trash"
          onClick={() => deleteCalendar.mutate({ calendarId: calendar.id })}
        />
      </ActionBox>
    </div>
  );
}
